import { Link } from "react-router-dom";
import { TRENT_LOGO_URL } from "@/components/Logo";

const SimplePage = ({ title, children }) => (
  <div className="min-h-screen bg-white text-[#0F172A]">
    <header className="border-b border-[#E5E7EB]">
      <div className="mx-auto max-w-[900px] px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={TRENT_LOGO_URL} alt="Trent" className="h-9 w-auto" />
        </Link>
        <Link to="/" className="text-xs uppercase tracking-wider text-[#475569] hover:text-[#0F172A]">← Home</Link>
      </div>
    </header>
    <main className="mx-auto max-w-[900px] px-6 py-12 prose prose-slate">
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">{title}</h1>
      {children}
    </main>
    <footer className="border-t border-[#E5E7EB] py-8 mt-12 text-sm text-[#475569]">
      <div className="mx-auto max-w-[900px] px-6 flex items-center justify-between">
        <span>© {new Date().getFullYear()} Trent Property Management · Ireland</span>
        <div className="flex gap-4">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
      </div>
    </footer>
  </div>
);

export function Privacy() {
  return (
    <SimplePage title="Privacy Policy">
      <p className="text-[#475569]">
        Last updated: {new Date().toISOString().slice(0, 10)}
      </p>
      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">1. Who we are</h2>
      <p>Trent Property Management is a property inventory and inspection service based in Ireland. Contact: <a href="mailto:info@trent.irish" className="text-[#16A34A] underline">info@trent.irish</a>.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">2. What we collect</h2>
      <ul className="list-disc pl-5 space-y-1 text-[#334155]">
        <li><b>Identity & contact</b>: your name, email, optional phone — provided when you register or are assigned to a property.</li>
        <li><b>Property data</b>: addresses, Eircodes, BER ratings, room conditions and inventory photos for properties you own, manage or rent.</li>
        <li><b>Inspection evidence</b>: photographs, written notes, meter readings, digital signatures, and PDF reports.</li>
        <li><b>Audit & access</b>: account creation date, consent timestamp, login timestamps.</li>
      </ul>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">3. Why we collect it</h2>
      <p>To produce, store and deliver property inspection reports, to evidence the state of a tenancy, to send signature requests, and to comply with Irish residential-tenancy obligations (lawful basis: Article 6(1)(b) and (f) GDPR).</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">4. Where it lives</h2>
      <p>Data is stored on EU-region infrastructure with encrypted access (HTTPS in transit, hashed credentials at rest). Personal data is not shared publicly — access is restricted to the authenticated owner, the customer it is assigned to, and authorised Trent administrators.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">5. Retention</h2>
      <p>Inspection photos, reports, tenant and landlord records are retained for the duration of the tenancy plus <b>6 years</b> (to meet potential RTB/tribunal evidentiary windows), after which they are anonymised or deleted. You can request earlier deletion at any time.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">6. Your rights (Articles 15–22 GDPR)</h2>
      <p>You can at any time, free of charge:</p>
      <ul className="list-disc pl-5 space-y-1 text-[#334155]">
        <li>Request a copy of your data — <b>Account → Export my data</b>.</li>
        <li>Request deletion — <b>Account → Request data deletion</b>.</li>
        <li>Request correction by contacting <a href="mailto:info@trent.irish" className="text-[#16A34A] underline">info@trent.irish</a>.</li>
        <li>Lodge a complaint with the Irish Data Protection Commission — <a href="https://www.dataprotection.ie/" className="text-[#16A34A] underline">dataprotection.ie</a>.</li>
      </ul>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">7. Cookies</h2>
      <p>We use strictly-necessary cookies (auth session). No marketing or analytics trackers without consent.</p>
    </SimplePage>
  );
}

export function Terms() {
  return (
    <SimplePage title="Terms of Service">
      <p className="text-[#475569]">Last updated: {new Date().toISOString().slice(0, 10)}</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">1. Service</h2>
      <p>Trent Property Management provides property inventory and inspection services and a digital platform for storing the resulting reports and evidence. Use of the platform constitutes acceptance of these terms.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">2. Accounts</h2>
      <p>You are responsible for keeping your credentials confidential. Notify us immediately of any unauthorised access.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">3. Your content</h2>
      <p>You retain ownership of any data, photos and documents you provide. By uploading them you grant us a non-exclusive licence to process them solely for delivering the service.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">4. Acceptable use</h2>
      <p>Do not use the platform to upload unlawful content, infringe a third party's rights, or attempt to access other users' data.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">5. Liability</h2>
      <p>The platform produces evidentiary documents — accuracy depends on the input you and our inspectors provide. We are not liable for indirect or consequential losses. Nothing in these terms excludes liability that cannot be excluded under Irish law.</p>

      <h2 className="trent-heading text-xl font-bold mt-8 mb-2">6. Governing law</h2>
      <p>These terms are governed by the laws of Ireland. The courts of Ireland have exclusive jurisdiction.</p>
    </SimplePage>
  );
}
